import {
  AgeGroupActType,
  CompensationActType,
  DayCaresActType,
  KlassesActType,
  RowActData,
} from 'app/dataTypes/reports/data/common/subsidies/act'

import { Entity } from './types'

type CommonCell = {
  columnKey: Entity
  value: string | Array<string>
}

const allColumnKeys = [
  Entity.Count,
  Entity.Category,
  Entity.Students,
  Entity.Compensation,
  Entity.TotalRow,
] as const

export const SECTION_ROW_KEY_PREFIX = 'section:' as const
export const ALLOWANCE_ROW_KEY_PREFIX = 'allowance-group:' as const
export const KLASSES_ALLOWANCE_ROW_KEY_PREFIX = 'klasses-allowance:' as const
export const AGE_GROUP_ROW_KEY_PREFIX = 'age-group:' as const

const createCell = (columnKey: Entity, value: string | Array<string> = ''): CommonCell => ({
  columnKey,
  value,
})

const createRowData = (values: Partial<Record<Entity, string | Array<string>>>) => {
  return allColumnKeys.reduce(
    (acc, columnKey) => {
      acc[columnKey] = createCell(columnKey, values[columnKey] ?? '')
      return acc
    },
    {} as Record<Entity, CommonCell>,
  )
}

const getStudentsValue = (compensationPerStudent: Array<CompensationActType>) => {
  return compensationPerStudent.map(({ students }) => String(students))
}

const getCompensationValue = (compensationPerStudent: Array<CompensationActType>) => {
  return compensationPerStudent.map(({ compensation }) => compensation)
}

const getTotalValue = (compensationPerStudent: Array<CompensationActType>, total: string) => {
  return [
    ...compensationPerStudent.map(({ totalCompensation }) => totalCompensation),
    '\u00A0',
    total || '0,00',
  ]
}

const createSectionRow = (
  classType: 'klasses' | 'dayCares',
  sectionNumber: string,
) => {
  const sectionTitle = classType === 'klasses' ? 'Дотационное питание' : 'ГПД'

  return {
    rowKey: `${SECTION_ROW_KEY_PREFIX}${classType}`,
    rowData: createRowData({
      [Entity.Count]: sectionNumber,
      [Entity.Category]: sectionTitle,
    }),
  }
}

const createAllowanceHeaderRow = (
  allowance: DayCaresActType,
  sectionNumber: string,
  allowanceIndex: number,
) => {
  const allowanceNumber = `${sectionNumber}.${allowanceIndex + 1}`

  return {
    rowKey: `${ALLOWANCE_ROW_KEY_PREFIX}${sectionNumber}:${allowanceIndex}`,
    rowData: createRowData({
      [Entity.Count]: allowanceNumber,
      [Entity.Category]: allowance.title,
    }),
  }
}

const createKlassesAllowanceRow = (
  allowance: KlassesActType,
  sectionNumber: string,
  allowanceIndex: number,
) => {
  const allowanceNumber = `${sectionNumber}.${allowanceIndex + 1}`

  return {
    rowKey: `${KLASSES_ALLOWANCE_ROW_KEY_PREFIX}${sectionNumber}:${allowanceIndex}`,
    rowData: createRowData({
      [Entity.Count]: allowanceNumber,
      [Entity.Category]: allowance.title,
      [Entity.Students]: getStudentsValue(allowance.compensationPerStudent),
      [Entity.Compensation]: getCompensationValue(allowance.compensationPerStudent),
      [Entity.TotalRow]: getTotalValue(allowance.compensationPerStudent, allowance.total),
    }),
  }
}

const createAgeGroupRow = (
  ageGroup: AgeGroupActType,
  sectionNumber: string,
  allowanceIndex: number,
  ageGroupIndex: number,
) => {
  const ageGroupNumber = `${sectionNumber}.${allowanceIndex + 1}.${ageGroupIndex + 1}`

  return {
    rowKey: `${AGE_GROUP_ROW_KEY_PREFIX}${sectionNumber}:${allowanceIndex}:${ageGroupIndex}`,
    rowData: createRowData({
      [Entity.Count]: ageGroupNumber,
      [Entity.Category]: `—  ${ageGroup.title}`,
      [Entity.Students]: getStudentsValue(ageGroup.compensationPerStudent),
      [Entity.Compensation]: getCompensationValue(ageGroup.compensationPerStudent),
      [Entity.TotalRow]: getTotalValue(ageGroup.compensationPerStudent, ageGroup.total),
    }),
  }
}

const createAllowanceData = (
  allowance: DayCaresActType,
  sectionNumber: string,
  allowanceIndex: number,
) => {
  return [
    createAllowanceHeaderRow(allowance, sectionNumber, allowanceIndex),
    ...allowance.ageGroups.map((ageGroup, ageGroupIndex) => (
      createAgeGroupRow(ageGroup, sectionNumber, allowanceIndex, ageGroupIndex)
    )),
  ]
}

const createKlassesSectionData = (
  items: Array<KlassesActType>,
  sectionNumber: string,
) => {
  if (!items.length) {
    return []
  }

  return [
    createSectionRow('klasses', sectionNumber),
    ...items.map((allowance, allowanceIndex) => (
      createKlassesAllowanceRow(allowance, sectionNumber, allowanceIndex)
    )),
  ]
}

const createDayCaresSectionData = (
  items: Array<DayCaresActType>,
  sectionNumber: string,
) => {
  if (!items.length) {
    return []
  }

  return [
    createSectionRow('dayCares', sectionNumber),
    ...items.flatMap((allowance, allowanceIndex) => (
      createAllowanceData(allowance, sectionNumber, allowanceIndex)
    )),
  ]
}

export const createData = (data: RowActData) => {
  return [
    ...createKlassesSectionData(data.klasses, '1'),
    ...createDayCaresSectionData(data.dayCares, '2'),
  ]
}
